import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useTutorial } from '../../context/TutorialContext';
import { notifyFollowers } from '../../lib/notifications';
import { useTheme } from '../../context/ThemeContext';
import CardImage from '../CardImage';
import { useHideNavWhile } from '../BottomSheet';

// Icons
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// Pre-selected tags (4-5 words, work for 1 or multiple cards)
const POST_TAGS = [
  { id: 'pulled', label: 'Just pulled this today!' },
  { id: 'found', label: 'Look what I found!' },
  { id: 'finally', label: 'Finally added to my collection!' },
  { id: 'mail', label: 'Mail day came through!' },
  { id: 'grail', label: 'The grail has been acquired!' },
  { id: 'trade', label: 'These are up for trade!' },
  { id: 'additions', label: 'New additions to the binder!' },
  { id: 'luck', label: 'Pack luck was on point!' },
  { id: 'showoff', label: 'Had to show these off!' },
  { id: 'rate', label: 'What do you all think?' },
];

// Generate UUID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const CreatePostModal = ({
  isOpen,
  onClose,
  userId,
  userCards = [],
  onSuccess,
  preSelectedCard = null, // Card to pre-select as #1
}) => {
  useHideNavWhile(isOpen);
  const { theme, isDark } = useTheme();
  const { handlePostAdded } = useTutorial();

  // Steps: 'select' -> 'tag' -> 'preview'
  const [step, setStep] = useState('select');

  // Card selection
  const [selectedCards, setSelectedCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Tag selection
  const [selectedTag, setSelectedTag] = useState(null);

  // UI state
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      // If preSelectedCard is passed, start with it selected as #1
      if (preSelectedCard) {
        setSelectedCards([preSelectedCard]);
      } else {
        setSelectedCards([]);
      }
      setSearchQuery('');
      setSelectedTag(null);
      setError(null);
    }
  }, [isOpen, preSelectedCard]);

  // Filter cards by search
  const filteredCards = userCards.filter(card =>
    card.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Toggle card selection
  const toggleCardSelection = (card) => {
    setSelectedCards(prev => {
      const isSelected = prev.find(c => c.id === card.id);
      if (isSelected) {
        return prev.filter(c => c.id !== card.id);
      } else if (prev.length < 9) {
        return [...prev, card];
      }
      return prev;
    });
  };

  // Check if card is selected
  const isCardSelected = (cardId) => {
    return selectedCards.some(c => c.id === cardId);
  };

  // Get selection order number
  const getSelectionOrder = (cardId) => {
    const index = selectedCards.findIndex(c => c.id === cardId);
    return index >= 0 ? index + 1 : null;
  };

  // Handle post creation
  const handlePost = async () => {
    if (selectedCards.length === 0 || !selectedTag) return;

    setIsPosting(true);
    setError(null);

    try {
      const postId = generateUUID();
      const cardIds = selectedCards.map(c => c.id);

      // Create post
      const { error: postError } = await supabase
        .from('posts')
        .insert({
          id: postId,
          user_id: userId,
          template_id: selectedTag.label,
          content: selectedTag.label,
          created_at: new Date().toISOString(),
        });

      if (postError) throw postError;

      // Link cards to post via junction table
      const postCardsData = cardIds.map(cardId => ({
        post_id: postId,
        card_id: cardId,
      }));

      const { error: cardsError } = await supabase
        .from('posts_cards')
        .insert(postCardsData);

      if (cardsError) throw cardsError;

      // Notify followers about new post
      const cardName = selectedCards[0]?.name || 'a card';
      const cardId = selectedCards[0]?.id;
      console.log('[CreatePost] Calling notifyFollowers:', { userId, cardName, cardId, postId });
      notifyFollowers(userId, cardName, cardId, postId);

      onSuccess?.();
      handlePostAdded(); // Trigger first post congrats if applicable
      onClose();
    } catch (err) {
      console.error('Error creating post:', err);
      setError(err.message || 'Failed to create post');
    } finally {
      setIsPosting(false);
    }
  };

  if (!isOpen) return null;

  const styles = getStyles(theme);

  // STEP 1: Select Cards
  const renderSelectStep = () => (
    <>
      <div style={styles.header}>
        <h2 style={styles.title}>Select Cards</h2>
        <button style={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      {/* Selected Cards Preview - Fan Stack */}
      <div style={styles.selectedPreview}>
        <div style={styles.cardStack}>
          {selectedCards.length === 0 ? (
            <div style={styles.placeholderCard}>
              <svg width="24" height="32" viewBox="0 0 24 32" fill="none" stroke="#3a3a3a" strokeWidth="1.5">
                <rect x="1" y="1" width="22" height="30" rx="3" />
                <path d="M6 12h12M6 16h12M6 20h8" strokeOpacity="0.5" />
              </svg>
            </div>
          ) : (
            <>
              {selectedCards.slice(0, 5).map((card, index) => (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, x: -20, scale: 0.8 }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    rotate: (index - Math.min(selectedCards.length - 1, 4) / 2) * 8,
                  }}
                  style={{
                    ...styles.stackCard,
                    zIndex: index,
                    marginLeft: index > 0 ? '-30px' : 0,
                  }}
                >
                  <CardImage
                    digitalImage={card.digital_image_url}
                    userPhoto={card.original_image_url}
                    name={card.name}
                    number={card.number}
                    setName={card.set_name}
                    preferPhoto
                    theme={theme}
                    imgStyle={styles.stackCardImage}
                  />
                </motion.div>
              ))}
              {selectedCards.length > 5 && (
                <div style={styles.moreIndicator}>
                  +{selectedCards.length - 5}
                </div>
              )}
            </>
          )}
        </div>
        <span style={styles.selectedCount}>
          {selectedCards.length === 0 ? 'Tap cards to select' : `${selectedCards.length}/9 selected`}
        </span>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search your cards..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Cards Grid */}
      <div style={styles.cardsScrollContainer}>
        <div style={styles.cardsGrid}>
          {filteredCards.map((card) => {
            const selected = isCardSelected(card.id);
            const order = getSelectionOrder(card.id);
            return (
              <motion.button
                key={card.id}
                style={{
                  ...styles.cardItem,
                  ...(selected ? styles.cardItemSelected : {}),
                }}
                onClick={() => toggleCardSelection(card)}
                whileTap={{ scale: 0.95 }}
              >
                <CardImage
                  digitalImage={card.digital_image_url}
                  userPhoto={card.original_image_url}
                  name={card.name}
                  number={card.number}
                  setName={card.set_name}
                  preferPhoto
                  theme={theme}
                  imgStyle={styles.cardImage}
                />
                {selected && (
                  <div style={styles.cardCheckmark}>
                    <span style={styles.cardOrder}>{order}</span>
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>

        {filteredCards.length === 0 && (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No cards found</p>
          </div>
        )}
      </div>

      {/* Continue Button */}
      <button
        style={{
          ...styles.primaryButton,
          ...(selectedCards.length === 0 ? styles.primaryButtonDisabled : {}),
        }}
        onClick={() => selectedCards.length > 0 && setStep('tag')}
        disabled={selectedCards.length === 0}
      >
        Continue with {selectedCards.length} card{selectedCards.length !== 1 ? 's' : ''}
      </button>
    </>
  );

  // STEP 2: Select Tag
  const renderTagStep = () => (
    <>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => setStep('select')}>
          <BackIcon />
        </button>
        <h2 style={styles.titleCentered}>Pick a Tag</h2>
        <button style={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      {/* Mini Card Preview */}
      <div style={styles.miniPreview}>
        {selectedCards.slice(0, 3).map((card, index) => (
          <div
            key={card.id}
            style={{
              ...styles.miniCard,
              marginLeft: index > 0 ? '-12px' : 0,
              zIndex: index,
            }}
          >
            <CardImage
              digitalImage={card.digital_image_url}
              userPhoto={card.original_image_url}
              name={card.name}
              number={card.number}
              setName={card.set_name}
              preferPhoto
              theme={theme}
              imgStyle={styles.miniCardImage}
            />
          </div>
        ))}
        {selectedCards.length > 3 && (
          <span style={styles.miniMore}>+{selectedCards.length - 3}</span>
        )}
      </div>

      {/* Tags */}
      <div style={styles.tagsContainer}>
        {POST_TAGS.map((tag) => (
          <motion.button
            key={tag.id}
            style={{
              ...styles.tagChip,
              ...(selectedTag?.id === tag.id ? styles.tagChipSelected : {}),
            }}
            onClick={() => setSelectedTag(tag)}
            whileTap={{ scale: 0.97 }}
          >
            {tag.label}
            {selectedTag?.id === tag.id && (
              <div style={styles.tagCheck}>
                <CheckIcon />
              </div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Continue Button */}
      <button
        style={{
          ...styles.primaryButton,
          ...(selectedTag === null ? styles.primaryButtonDisabled : {}),
        }}
        onClick={() => selectedTag && setStep('preview')}
        disabled={selectedTag === null}
      >
        Preview Post
      </button>
    </>
  );

  // STEP 3: Preview & Post
  const renderPreviewStep = () => (
    <>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => setStep('tag')}>
          <BackIcon />
        </button>
        <h2 style={styles.titleCentered}>Preview</h2>
        <button style={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      {/* Post Preview */}
      <div style={styles.previewContainer}>
        {/* Tag */}
        <div style={styles.previewTag}>
          {selectedTag?.label}
        </div>

        {/* Cards Display */}
        <div style={styles.previewCards}>
          {selectedCards.length === 1 ? (
            // Single card - large centered
            <div style={styles.singleCard}>
              <CardImage
                digitalImage={selectedCards[0].digital_image_url}
                userPhoto={selectedCards[0].original_image_url}
                name={selectedCards[0].name}
                number={selectedCards[0].number}
                setName={selectedCards[0].set_name}
                theme={theme}
                imgStyle={styles.singleCardImage}
              />
            </div>
          ) : selectedCards.length <= 4 ? (
            // 2-4 cards - 2x2 grid
            <div style={styles.gridSmall}>
              {selectedCards.map((card) => (
                <div key={card.id} style={styles.gridCard}>
                  <CardImage
                    digitalImage={card.digital_image_url}
                    userPhoto={card.original_image_url}
                    name={card.name}
                    number={card.number}
                    setName={card.set_name}
                    preferPhoto
                    theme={theme}
                    imgStyle={styles.gridCardImage}
                  />
                </div>
              ))}
            </div>
          ) : (
            // 5-9 cards - 3x3 grid
            <div style={styles.gridLarge}>
              {selectedCards.map((card) => (
                <div key={card.id} style={styles.gridCardSmall}>
                  <CardImage
                    digitalImage={card.digital_image_url}
                    userPhoto={card.original_image_url}
                    name={card.name}
                    number={card.number}
                    setName={card.set_name}
                    preferPhoto
                    theme={theme}
                    imgStyle={styles.gridCardImage}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <p style={styles.errorText}>{error}</p>}

      {/* Post Button */}
      <button
        style={{
          ...styles.postButton,
          ...(isPosting ? styles.postButtonDisabled : {}),
        }}
        onClick={handlePost}
        disabled={isPosting}
      >
        {isPosting ? (
          'Posting...'
        ) : (
          <>
            <SendIcon />
            <span>Post</span>
          </>
        )}
      </button>
    </>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={styles.backdrop}
          onClick={onClose}
        >
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 34, stiffness: 380, mass: 0.9 }}
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Step Indicator */}
            <div style={styles.stepIndicator}>
              <div style={{
                ...styles.stepDot,
                ...(step === 'select' ? styles.stepDotActive : styles.stepDotComplete),
              }} />
              <div style={styles.stepLine} />
              <div style={{
                ...styles.stepDot,
                ...(step === 'tag' ? styles.stepDotActive : step === 'preview' ? styles.stepDotComplete : {}),
              }} />
              <div style={styles.stepLine} />
              <div style={{
                ...styles.stepDot,
                ...(step === 'preview' ? styles.stepDotActive : {}),
              }} />
            </div>

            {step === 'select' && renderSelectStep()}
            {step === 'tag' && renderTagStep()}
            {step === 'preview' && renderPreviewStep()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const getStyles = (theme) => ({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    width: '100%',
    maxWidth: '400px',
    maxHeight: 'calc(100vh - 180px)',
    marginBottom: 'calc(80px + var(--safe-area-bottom, 0px))',
    backgroundColor: theme.bgSheet,
    borderRadius: '24px',
    padding: '16px 20px 24px',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${theme.border}`,
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0',
    marginBottom: '16px',
  },
  stepDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: theme.border,
    transition: 'all 0.2s ease',
  },
  stepDotActive: {
    backgroundColor: theme.accent,
    boxShadow: '0 0 8px rgba(14, 165, 233, 0.5)',
  },
  stepDotComplete: {
    backgroundColor: theme.accent,
  },
  stepLine: {
    width: '40px',
    height: '2px',
    backgroundColor: theme.border,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexShrink: 0,
  },
  title: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  titleCentered: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
    flex: 1,
    textAlign: 'center',
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  backButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.text,
  },
  // Selected Preview (Step 1)
  selectedPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: theme.bgCard,
    borderRadius: '16px',
    border: `1px solid ${theme.border}`,
  },
  cardStack: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stackCard: {
    width: '50px',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
  placeholderCard: {
    width: '50px',
    height: '70px',
    borderRadius: '6px',
    border: `2px dashed ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.bgCard,
  },
  stackCardImage: {
    width: '100%',
    display: 'block',
  },
  moreIndicator: {
    marginLeft: '8px',
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  selectedCount: {
    color: theme.textMuted,
    fontSize: '13px',
  },
  // Search
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    marginBottom: '12px',
    color: theme.textSecondary,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: theme.text,
    fontSize: '14px',
  },
  // Cards Grid
  cardsScrollContainer: {
    flex: 1,
    overflowY: 'auto',
    marginBottom: '16px',
    minHeight: '150px',
    maxHeight: '45vh',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    paddingBottom: '8px',
  },
  cardItem: {
    position: 'relative',
    aspectRatio: '0.714',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '2px solid transparent',
    cursor: 'pointer',
    padding: 0,
    backgroundColor: 'transparent',
  },
  cardItemSelected: {
    border: `2px solid ${theme.accent}`,
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cardCheckmark: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: theme.accent,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardOrder: {
    color: theme.text,
    fontSize: '12px',
    fontWeight: '700',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  emptyText: {
    color: theme.textSecondary,
    fontSize: '14px',
  },
  // Mini Preview (Step 2)
  miniPreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  miniCard: {
    width: '44px',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  miniCardImage: {
    width: '100%',
    display: 'block',
  },
  miniMore: {
    marginLeft: '8px',
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '500',
  },
  // Tags
  tagsContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    overflowY: 'auto',
    marginBottom: '16px',
  },
  tagChip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    color: theme.text,
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s ease',
  },
  tagChipSelected: {
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    border: `1px solid ${theme.accent}`,
    color: theme.accent,
  },
  tagCheck: {
    color: theme.accent,
  },
  // Preview (Step 3)
  previewContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    overflowY: 'auto',
    marginBottom: '16px',
  },
  previewTag: {
    padding: '10px 20px',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    border: `1px solid ${theme.accent}`,
    borderRadius: '20px',
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  previewCards: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  singleCard: {
    width: '180px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
  },
  singleCardImage: {
    width: '100%',
    display: 'block',
  },
  gridSmall: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
    maxWidth: '240px',
  },
  gridLarge: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
    maxWidth: '280px',
  },
  gridCard: {
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },
  gridCardSmall: {
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  gridCardImage: {
    width: '100%',
    display: 'block',
  },
  // Buttons
  primaryButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '12px',
    color: theme.text,
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
  },
  primaryButtonDisabled: {
    backgroundColor: theme.border,
    color: theme.textSecondary,
    cursor: 'not-allowed',
  },
  postButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '12px',
    color: theme.text,
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  postButtonDisabled: {
    backgroundColor: theme.border,
    cursor: 'not-allowed',
  },
  errorText: {
    color: theme.error,
    fontSize: '13px',
    margin: '0 0 12px 0',
    textAlign: 'center',
  },
});

export default CreatePostModal;
